/**
 * Recipe library — the app's home (DESIGN_GUIDELINES.md §9.1).
 *
 * Search, favourites and tag filters live here rather than on the detail page, so there is one
 * consistent place to narrow the collection.
 */

import { useMemo, useState } from 'react';
import { Alert, Button, Group, Loader, SegmentedControl, SimpleGrid, Stack, Text, TextInput, ThemeIcon, Title } from '@mantine/core';
import { PageShell } from '../components/PageShell';
import { RecipeCard } from '../components/RecipeCard';
import { fetchCollection, updateRecipeSettings, type SessionUser } from '../lib/api';
import { useAsync } from '../lib/useAsync';

type Filter = 'all' | 'favourites' | 'ready';

export function LibraryPage({
  user,
  onSignOut,
}: {
  user: SessionUser;
  onSignOut: () => void;
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const collection = useAsync(() => fetchCollection(), []);

  const visible = useMemo(() => {
    const items = collection.data ?? [];
    const needle = query.trim().toLowerCase();

    return items.filter((item) => {
      if (filter === 'favourites' && !item.isFavourite) return false;
      // "Ready" = full-fidelity import, so the serving switcher and shopping list will work.
      if (filter === 'ready' && item.isPartial) return false;
      if (!needle) return true;
      return (
        item.name.toLowerCase().includes(needle) ||
        (item.headline ?? '').toLowerCase().includes(needle)
      );
    });
  }, [collection.data, filter, query]);

  async function toggleFavourite(id: string, next: boolean) {
    // Optimistic: the heart should respond instantly.
    collection.setData(
      (collection.data ?? []).map((item) => (item.id === id ? { ...item, isFavourite: next } : item)),
    );
    try {
      await updateRecipeSettings(id, { isFavourite: next });
    } catch {
      collection.reload();
    }
  }

  const total = collection.data?.length ?? 0;

  return (
    <PageShell
      user={user}
      onSignOut={onSignOut}
      title="Your recipes"
      actions={
        <Button component="a" href="/import" radius="xl">
          Import a recipe
        </Button>
      }
    >
      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search your recipes"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          style={{ flex: '1 1 220px', maxWidth: 360 }}
          aria-label="Search your recipes"
        />
        <SegmentedControl
          value={filter}
          onChange={(value) => setFilter(value as Filter)}
          color="dark"
          data={[
            { label: 'All', value: 'all' },
            { label: 'Favourites', value: 'favourites' },
            { label: 'Ready', value: 'ready' },
          ]}
        />
      </Group>

      {collection.error ? (
        <Alert color="red" variant="light" role="alert">
          {collection.error}
        </Alert>
      ) : null}

      {collection.loading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : null}

      {!collection.loading && total === 0 ? (
        <Stack align="center" gap="sm" py="xl">
          <ThemeIcon size={56} radius="xl" variant="light" color="hfGreen">
            <span aria-hidden="true" style={{ fontSize: 24 }}>
              🍳
            </span>
          </ThemeIcon>
          <Title order={3}>No recipes yet</Title>
          <Text size="sm" c="var(--ff-muted)" ta="center" maw={420}>
            Paste a HelloFresh recipe link and the planner will download it — ingredients,
            instructions and photos — so it keeps working offline.
          </Text>
          <Button component="a" href="/import" radius="xl" mt="xs">
            Import your first recipe
          </Button>
        </Stack>
      ) : null}

      {!collection.loading && total > 0 && visible.length === 0 ? (
        <Text c="var(--ff-muted)" py="xl" ta="center">
          Nothing matches that search.
        </Text>
      ) : null}

      {visible.length > 0 ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {visible.map((item) => (
            <RecipeCard
              key={item.id}
              item={item}
              onToggleFavourite={(next) => void toggleFavourite(item.id, next)}
            />
          ))}
        </SimpleGrid>
      ) : null}
    </PageShell>
  );
}
